import { apiClient } from '../utils/api';

export interface Chatbot {
  id: string;
  name: string;
  description: string;
  industry?: string;
  use_case?: string;
  tone?: string;
  data_source_ids?: string[];
  api_key?: string;
  status?: string;
  system_prompt?: string;
  response_source?: string;
  performance_mode?: 'speed' | 'balance' | 'accuracy';
  performanceMode?: 'speed' | 'balance' | 'accuracy';
  created_at?: string;
  updated_at?: string;
  total_messages?: string;
}

export interface ChatHistoryItem {
  chatbotId: string;
  sessionId: string;
  userMessage: string;
  timestamp: string;
}

export interface SessionHistoryItem {
  id: string;
  userMessage: string;
  botResponse: string;
  generatedSql?: string;
  tokens?: {
    prompt: number;
    completion: number;
    total: number;
  };
  generationTimeMs?: number;
  timestamp: string;
}

export interface ChatHistoryResponse {
  success: boolean;
  data: {
    messages: ChatHistoryItem[];
  };
  timestamp: string;
  statusCode: number;
}

export interface SessionHistoryResponse {
  success: boolean;
  data: {
    sessionId: string;
    chatbotId: string;
    messages: SessionHistoryItem[];
  };
  timestamp: string;
  statusCode: number;
}

export interface CreateChatbotRequest {
  name: string;
  description: string;
  industry: string;
  useCase: string;
  tone: string;
  dataSourceIds: string[];
  responseSource?: string;
}

export interface UpdateChatbotRequest {
  name?: string;
  description?: string;
  industry?: string;
  systemPrompt?: string;
  status?: string;
  responseSource?: string;
  performanceMode?: 'speed' | 'balance' | 'accuracy';
  dataSourceIds?: string[];
}

class ChatbotService {
  async createChatbot(data: CreateChatbotRequest): Promise<Chatbot> {
    const response = await apiClient.post('/api/v1/chatbots', data);
    return response.data;
  }

  async updateChatbot(id: string, data: UpdateChatbotRequest): Promise<Chatbot> {
    const response = await apiClient.put(`/api/v1/chatbots/${id}`, data);
    return response.data;
  }

  async getChatbots(): Promise<Chatbot[]> {
    const response = await apiClient.get('/api/v1/chatbots');
    return response.data;
  }

  async getChatbot(id: string): Promise<Chatbot> {
    const response = await apiClient.get(`/api/v1/chatbots/${id}`);
    return response.data;
  }

  async deleteChatbot(id: string): Promise<void> {
    await apiClient.delete(`/api/v1/chatbots/${id}`);
  }

  async getChatHistory(chatbotId: string): Promise<ChatHistoryResponse> {
    const response = await apiClient.get<ChatHistoryResponse>(`/api/v1/messages/history/chatbot?chatbotId=${chatbotId}`);
    return response;
  }

  async getSessionHistory(sessionId: string, chatbotId: string): Promise<SessionHistoryResponse> {
    const response = await apiClient.get<SessionHistoryResponse>(`/api/v1/messages/history/session?sessionId=${sessionId}&chatbotId=${chatbotId}`);
    return response;
  }

  async deleteSessionHistory(sessionId: string, chatbotId: string): Promise<void> {
    await apiClient.delete(`/api/v1/messages/history/session?sessionId=${sessionId}&chatbotId=${chatbotId}`);
  }
}

const chatbotService = new ChatbotService();
export default chatbotService;

// Export the class and types to make this a module
export { ChatbotService };
